import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { FEATURE, PACKAGE_CHANGE_TYPES } from '@src/constants/package';
import useUserInfo from '@src/hooks/useTtsUser';
import { Typography } from '@mui/material';
import {
  checkCustomPackage,
  checkFreePackage,
  getPackageChangeType,
} from '@src/services/package';
import { useSelector } from 'react-redux';

const V3BuyPackageContent = ({
  usingPackage,
  packageState,
  isAutoRenewing,
  orderPackageInfoWhenApplyVoucher,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { ttsUser } = useUserInfo();
  const { user = {} } = useSelector((state) => state.auth);

  const packageChangeType = getPackageChangeType({
    currPackage: packageState,
    prevPackage: usingPackage,
    prevPackageExpiredDate: user?.packageExpiryDate,
  });

  const isUsingCustomPackage = checkCustomPackage({
    level: usingPackage.level,
    type: usingPackage.type,
  });

  const isBuyingCustomPackage = checkCustomPackage({
    level: packageState.level,
    type: packageState.type,
  });

  const isFreePackage = checkFreePackage(user.packageCode);
  const oneTimeCredits = isFreePackage
    ? user?.lockCharacters
    : user?.remainingCharacters + user?.bonusCharacters || user?.lockCharacters;

  const expiredDatePackage = () => {
    if (isBuyingCustomPackage)
      return moment().add(1, 'year').format('DD/MM/YYYY');

    return moment()
      .add(orderPackageInfoWhenApplyVoucher?.cycle?.totalCycles, 'months')
      .format('DD/MM/YYYY');
  };

  const getNewPackageExpiryDate = () => ({
    key: 'newExpirationDate',
    content: t('newExpirationDate', {
      expiredDate: expiredDatePackage(),
    }),
    status: 'success',
  });

  const getVoiceLevelContent = (currPkg, buyPkg) => {
    const isCurrPkgHasBasicGlobalVoice = currPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isCurrPkgHasAdvanceGlobalVoice = currPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );
    const isNewPkgHasBasicGlobalVoice = buyPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isNewPkgHasAdvanceGlobalVoice = buyPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    let voiceLevelContent = '';
    let voiceLevelStatus = 'success';

    if (!currPkg.cycle) {
      if (isNewPkgHasAdvanceGlobalVoice)
        voiceLevelContent = t('supportBasicAndAdvancedGlobalVoice');
      else if (isNewPkgHasBasicGlobalVoice)
        voiceLevelContent = t('supportBasicGlobalVoice');
      else return null;

      return { content: voiceLevelContent, status: voiceLevelStatus };
    }

    const isSupportBasicAndAdvanceGlobalVoice = isNewPkgHasAdvanceGlobalVoice;
    const isNotSupportAdvancedGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      isNewPkgHasBasicGlobalVoice;
    const isSupportBasicGlobalVoice =
      isNewPkgHasBasicGlobalVoice &&
      !isSupportBasicAndAdvanceGlobalVoice &&
      !isCurrPkgHasAdvanceGlobalVoice;

    const isNotSupportBasicAndAdvanceGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasBasicGlobalVoice;

    const isNotSupportBasicGlobalVoice =
      isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;

    const notChange =
      !isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;
    if (notChange) return null;

    if (isSupportBasicAndAdvanceGlobalVoice)
      voiceLevelContent = t('supportBasicAndAdvancedGlobalVoice');
    else if (isNotSupportAdvancedGlobalVoice) {
      voiceLevelContent = t('notSupportAdvancedGlobalVoice');
      voiceLevelStatus = 'error';
    } else if (isSupportBasicGlobalVoice)
      voiceLevelContent = t('supportBasicGlobalVoice');
    else if (isNotSupportBasicAndAdvanceGlobalVoice) {
      voiceLevelContent = t('notSupportBasicAndAdvancedGlobalVoice');
      voiceLevelStatus = 'error';
    } else if (isNotSupportBasicGlobalVoice) {
      voiceLevelContent = t('notSupportBasicGlobalVoice');
      voiceLevelStatus = 'error';
    }
    if (!voiceLevelContent) return null;

    return { content: voiceLevelContent, status: voiceLevelStatus };
  };

  const getVoiceCloningContent = (currPkg, buyPkg) => {
    // Extract voice cloning properties
    const voiceCloning = {
      current: {
        hasFeature: currPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: currPkg?.maxCloningVoices || 0,
      },
      new: {
        hasFeature: buyPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: buyPkg?.maxCloningVoices || 0,
      },
    };

    // Handle one-time package case
    if (
      !currPkg.cycle &&
      !isUsingCustomPackage &&
      !isFreePackage &&
      !isBuyingCustomPackage
    ) {
      return voiceCloning.new.hasFeature
        ? {
            content: t('accessToVoiceCloning'),
            status: 'success',
          }
        : null;
    }

    // Handle case where both packages have voice cloning
    if (voiceCloning.new.hasFeature && voiceCloning.current.hasFeature) {
      const compareVoices = {
        increased: voiceCloning.current.voices < voiceCloning.new.voices,
        decreased: voiceCloning.current.voices > voiceCloning.new.voices,
        unchanged: voiceCloning.current.voices === voiceCloning.new.voices,
      };

      if (compareVoices.increased) {
        return {
          content: t('increasedFromVoiceCloning', {
            fromVoiceCloning: voiceCloning.current.voices,
            toVoiceCloning: voiceCloning.new.voices,
          }),
          status: 'success',
        };
      }

      if (compareVoices.decreased) {
        return {
          content: t('decreasedFromVoiceCloning', {
            fromVoiceCloning: voiceCloning.current.voices,
            toVoiceCloning: voiceCloning.new.voices,
          }),
          status: 'error',
        };
      }

      if (compareVoices.unchanged) {
        return {
          content: t('useVoiceCloning', {
            voiceCloning: voiceCloning.new.voices,
          }),
          status: 'success',
        };
      }
    }

    if (voiceCloning.new.hasFeature && isBuyingCustomPackage)
      return {
        content: t('useVoiceCloning', {
          voiceCloning: voiceCloning.new.voices,
        }),
        status: 'success',
      };

    if (voiceCloning.new.hasFeature && !voiceCloning.current.hasFeature)
      return {
        content: t('accessToVoiceCloning'),
        status: 'success',
      };

    // Handle case where feature is being removed
    if (voiceCloning.current.hasFeature && !voiceCloning.new.hasFeature) {
      return {
        content: t('noAccessToVoiceCloning'),
        status: 'error',
      };
    }

    return null;
  };

  const getQuotaChangeContent = (currPkg, buyPkg) => {
    const currQuota = currPkg?.cycle?.creditsPerCycle || 0;
    const buyQuota = isBuyingCustomPackage
      ? buyPkg.maxCredits || 0
      : buyPkg?.cycle?.creditsPerCycle || 0;
    const isSamePackage = currPkg.code === buyPkg.code;

    if (isBuyingCustomPackage) {
      if (oneTimeCredits > 0) {
        return {
          content: t('rolloverCreditsOnetimeWithCustom', {
            newCredits: buyQuota.toLocaleString(language),
            oldCredits: oneTimeCredits.toLocaleString(language),
          }),
          status: 'success',
        };
      }
      return {
        content: t('maxCreditsPackage', {
          credits: buyQuota.toLocaleString(language),
        }),
        status: 'success',
      };
    }

    if (!currPkg.cycle || isSamePackage) {
      const content =
        oneTimeCredits > 0
          ? t('newCycleCharactersAndV2Characters', {
              fromCharacter: buyQuota.toLocaleString(language),
              toCharacter: oneTimeCredits.toLocaleString(language),
            })
          : t('charactersPerMonth', {
              character: buyQuota.toLocaleString(language),
            });
      return { content, status: 'success' };
    }
    if (currQuota < buyQuota)
      return {
        content: t('increasedFromCharactersPerMonth', {
          fromCharacter: currQuota?.toLocaleString(language),
          toCharacter: buyQuota?.toLocaleString(language),
        }),
        status: 'success',
      };
    if (currQuota > buyQuota)
      return {
        content: t('decreasedFromCharactersPerMonth', {
          fromCharacter: currQuota?.toLocaleString(language),
          toCharacter: buyQuota?.toLocaleString(language),
        }),
        status: 'error',
      };
    return {
      content: t('charactersPerMonth', {
        character: currQuota.toLocaleString(language),
      }),
      status: 'success',
    };
  };

  const getDeviceChangeContent = (currPkg, buyPkg) => {
    const currDevice = currPkg?.device;
    const buyDevice = buyPkg?.device;

    if (!currPkg.cycle && !isUsingCustomPackage) {
      return {
        content: t('deviceNumberCurrent', { device: buyDevice }),
        status: 'success',
      };
    }
    if (currDevice < buyDevice)
      return {
        content: t('increasedDeviceFromTo', {
          fromDevice: currDevice,
          toDevice: buyDevice,
        }),
        status: 'success',
      };
    if (currDevice > buyDevice)
      return {
        content: t('decreasedDeviceFromTo', {
          fromDevice: currDevice,
          toDevice: buyDevice,
        }),
        status: 'error',
      };

    return {
      content: t('deviceNumberCurrent', { device: currDevice }),
      status: 'success',
    };
  };

  const getAutoRenewingContent = () => {
    const duration =
      packageState.expiresIn === 30 ? t('billedMonthly') : t('billedAnnually');
    const nextPaymentDate = moment()
      .add(packageState?.cycle?.totalCycles, 'months')
      .format('DD/MM/YYYY');

    return {
      content: t('startDateAutoRenewing', {
        duration,
        startDate: nextPaymentDate,
      }),
      status: 'warning',
    };
  };

  const getNotiAdvanceGlobalVoice = (buyPkg) => {
    const isHasFeatureAdvanceGlobalVoice = buyPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );
    if (!isHasFeatureAdvanceGlobalVoice) return null;
    if (!oneTimeCredits) return null;

    return {
      content: t('noteOneTimeCreditWithVipPackage'),
      status: 'warning',
    };
  };

  const getPackageChangeContent = (currPkg, buyPkg) => {
    const contents = [];

    if (!isAutoRenewing) contents.push(getNewPackageExpiryDate());

    const voiceLevelContent = getVoiceLevelContent(currPkg, buyPkg);
    if (voiceLevelContent) contents.push(voiceLevelContent);

    const voiceCloningContent = getVoiceCloningContent(currPkg, buyPkg);
    if (voiceCloningContent) contents.push(voiceCloningContent);
    contents.push(getQuotaChangeContent(currPkg, buyPkg));
    if (isBuyingCustomPackage)
      contents.push({
        content: t('noteCustomPackageDuration'),
        status: 'success',
      });
    contents.push(getDeviceChangeContent(currPkg, buyPkg));

    // Check topUp credits
    const topUpCredits = ttsUser?.studio?.topUp?.credits || 0;
    if (topUpCredits > 0)
      contents.push({
        content: t('additionalCharactersWillBeLost', {
          credits: topUpCredits?.toLocaleString(language),
        }),
        status: 'error',
      });

    // New package expiry date
    if (!isFreePackage)
      contents.push({
        content: t('theCurrentPlanExpirationDateWillBeReplaced'),
        status: 'warning',
      });

    // Note about vip package and one time credits
    const notiAdvanceGlobalVoice = getNotiAdvanceGlobalVoice(
      buyPkg,
      oneTimeCredits,
    );
    if (notiAdvanceGlobalVoice) contents.push(notiAdvanceGlobalVoice);

    if (!currPkg.cycle && oneTimeCredits) {
      contents.push({
        content: t('allCharactersWillNotBeRetainedWhenTheNewPlanExpires'),
        status: 'warning',
      });
    }

    if (isAutoRenewing) contents.push(getAutoRenewingContent());

    return contents;
  };

  const changePackageContents = getPackageChangeContent(
    usingPackage,
    packageState,
  );

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleRoundedIcon className="icon-check-circle" />;
      case 'error':
        return <CancelRoundedIcon className="icon-cancel-circle" />;
      case 'warning':
        return <ErrorRoundedIcon className="icon-error-circle" />;
      default:
        return <CheckCircleRoundedIcon className="icon-error-circle" />;
    }
  };

  const renderWarningNote = () => {
    if (!usingPackage.cycle && !isUsingCustomPackage) return null;
    const isUpgrade = packageChangeType === PACKAGE_CHANGE_TYPES.UPGRADE;
    const isShowWarningLockCredits = isUpgrade || isUsingCustomPackage;
    if (!isShowWarningLockCredits) return null;
    return (
      <div className="note-warning-lock-circle">
        <WarningRoundedIcon className="icon-warning-lock-circle" />
        <div className="contents">
          <Typography
            component="span"
            className="text-note"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                oneTimeCredits
                  ? t('noteWarningLockCycleHasOnetime', {
                      oneTimeCredits: oneTimeCredits?.toLocaleString(language),
                    })
                  : t('noteWarningLockCycle'),
              ),
            }}
          />
          <Typography className="text-note">
            {t('revokeTotalCreditsWhenOrderPaid')}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div className="list">
      {changePackageContents?.map((item) => (
        <div key={item.key} className="item">
          {renderStatusIcon(item.status)}
          <Typography
            component="span"
            dangerouslySetInnerHTML={{
              __html: item.content,
            }}
          />
        </div>
      ))}
      {renderWarningNote()}
    </div>
  );
};

export default V3BuyPackageContent;
